<template>
	<div class="meeting-card-wrapper">
		<div class="meeting-icon">
			<VideoIcon color="#ffffff" />
		</div>
		<div class="meeting-card-content">
			<div class="meeting-card-loading" v-if="!videosession">
				<Spinner />
			</div>
			<div class="meeting-card-info" v-if="videosession">
				<div class="meeting-title">
					<div
						v-if="title || meetingEnded"
						v-text="title || this.$t('chat_livestream_ended')"
					/>
					<i18n-t
						v-if="!meetingEnded && !title"
						keypath="chat_meeting_was_hosted"
						class="hosted"
						tag="div"
					>
						<template #UserName>
							<UserName :user="host" />
						</template>
					</i18n-t>
				</div>
				<div
					class="meeting-duration"
					v-text="$t('chat_livestream_duration', [duration])"
				/>

				<div class="meeting-extras">
					<slot name="profiles" :userIds="userIds" :max="userMax">
						<ProfileList
							v-if="!isSuper"
							:userIds="userIds"
							:max="userMax"
							condensed
							clickable
							@click="$emit('participants')"
						/>
						<div
							v-if="isSuper"
							class="participant-count"
							v-text="
								$tc(
									'meetings_viewerslist_title.variable',
									videosession.users_count,
									[videosession.users_count]
								)
							"
						/>
					</slot>
					<div
						v-if="hasArchives"
						class="meeting-replay"
						@click="$emit('replay')"
						v-t="'chat_livestream_replay'"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ProfileList from './ProfileList'
import { getUser, getChat } from '@/store'
import { VideoIcon } from '@/icons'
import { UserName, Spinner } from '@/components/common'

const USER_MAX = 18
const USER_MAX_WITH_ARCHIVE = 15

export default {
	name: 'MeetingEndedCard',
	components: { ProfileList, VideoIcon, UserName, Spinner },
	props: {
		videosession: Object,
		title: String,
	},
	emits: ['participants', 'replay'],
	computed: {
		host() {
			return getUser(this.videosession.host_id)
		},
		meetingEnded() {
			return !this.videosession?.host_id
		},
		isSuper() {
			const chat = getChat(this.videosession?.bubble_id) || {}
			return chat?.is_supergroup ?? false
		},
		userMax() {
			return this.hasArchives ? USER_MAX_WITH_ARCHIVE : USER_MAX
		},
		duration() {
			if (!this.videosession) return ''

			let start = this.$m(this.videosession.created_at)
			let end = this.$m(this.videosession.ended_at)
			if (start.isValid() && end.isValid()) {
				return this.$m.duration(end.diff(start)).humanize()
			} else {
				return ''
			}
		},
		userIds() {
			const { users = [] } = this.videosession ?? {}
			return users
		},

		hasArchives() {
			if (!this.videosession) return false
			const { archives = [] } = this.videosession
			return archives.some((a) => a.status === 'FINISHED')
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.meeting-card-wrapper {
	@include flex;
	align-items: stretch;
	@include radius;
	width: 415px;
	min-height: 80px;
	overflow: hidden;
	border: 1px solid var(--neutral-200);

	.meeting-icon {
		@include flex-center;
		background: var(--neutral-500);
		width: 46px;
		flex-shrink: 0;
		.pronto-icon {
			font-size: 28px;
			color: white;
		}
	}

	.meeting-card-content {
		position: relative;
		flex-grow: 1;
		.meeting-card-loading {
			@include flex-center;
			@include absolute-cover;
		}
	}

	.meeting-card-info {
		padding: 8px;
		width: 100%;
		height: 100%;
		overflow: hidden;
		@include flex-column-spread;
		.meeting-title {
			.hosted {
				@include flex;
				column-gap: 5px; // only add gap between words horizontally, not vertically when wrapping
				flex-wrap: wrap;
			}
			.user-display-name {
				display: inline-block !important;
				@include ellipsis;
			}
		}
		.meeting-duration {
			color: var(--neutral-500);
			font-size: 12px;
		}
		.meeting-extras {
			@include flex-spread;
			.meeting-replay {
				cursor: pointer;
				height: 24px;
				@include radius;
				text-align: center;
				background: var(--neutral-500);
				color: white;
				font-size: 12px;
				padding: 4px 16px;
			}
			.participant-count {
				font-size: 12px;
				color: var(--neutral-500);
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.meeting-card-wrapper {
		.meeting-icon {
			background: var(--neutral-300);
		}
	}
}
</style>
